/**
 * Idle Timeout Hook
 * Detects user inactivity and triggers warnings/logout
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getIdleTimeoutConfig, minutesToMs, IdleTimeoutConfig } from '../lib/auth/idleTimeoutConfig';
import { logger } from '../lib/utils/logUtils';

interface UseIdleTimeoutOptions {
  onWarning: () => void;
  onTimeout: () => void;
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  remainingTime: number;
  isWarning: boolean;
  resetTimer: () => void;
  config: IdleTimeoutConfig | null;
}

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

export function useIdleTimeout({
  onWarning,
  onTimeout,
  enabled = true
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [config, setConfig] = useState<IdleTimeoutConfig | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isWarning, setIsWarning] = useState(false);

  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Load configuration only when enabled
  useEffect(() => {
    if (!enabled) {
      setConfig(null);
      return;
    }
    
    const loadConfig = async () => {
      try {
        const loadedConfig = await getIdleTimeoutConfig();
        setConfig(loadedConfig);
        logger.info('Idle timeout config loaded', {
          timeout_minutes: loadedConfig.timeout_minutes,
          warning_minutes: loadedConfig.warning_minutes,
          enabled: loadedConfig.enabled
        });
      } catch (error) {
        logger.error('Failed to load idle timeout config:', error);
      }
    };

    loadConfig();
  }, [enabled]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    if (!config || !config.enabled || !enabled) {
      return;
    }

    clearTimers();
    setIsWarning(false);
    lastActivityRef.current = Date.now();

    const timeoutMs = minutesToMs(config.timeout_minutes);
    const warningMs = minutesToMs(config.timeout_minutes - config.warning_minutes);

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      // Set initial remaining time
      const warningDurationMs = minutesToMs(config.warning_minutes);
      setRemainingTime(warningDurationMs);
      setIsWarning(true);
      onWarning();
      logger.info('Idle timeout warning triggered');

      // Start countdown for remaining time
      const warningTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - warningTime;
        const remaining = warningDurationMs - elapsed;
        
        if (remaining <= 0) {
          setRemainingTime(0);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        } else {
          setRemainingTime(remaining);
        }
      }, 1000);
    }, warningMs);

    // Set timeout timer
    timeoutTimerRef.current = setTimeout(() => {
      logger.info('Idle timeout triggered - logging out user');
      onTimeout();
    }, timeoutMs);

    logger.debug('Idle timer reset', {
      timeout_minutes: config.timeout_minutes,
      warning_minutes: config.warning_minutes
    });
  }, [config, enabled, clearTimers, onWarning, onTimeout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Throttle activity detection to avoid excessive resets (max once per 5 seconds)
    if (timeSinceLastActivity < 5000) return;

    lastActivityRef.current = now;

    // Always reset the timer on activity
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!config || !config.enabled || !enabled) {
      clearTimers();
      return;
    }
    
    // Initial timer setup
    resetTimer();

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [config, enabled, resetTimer, handleActivity, clearTimers]);

  return {
    remainingTime,
    isWarning,
    resetTimer,
    config
  };
}